import {useMemo} from 'react'
import isEqual from 'lodash/isEqual.js'

import {
  REPORT_EXPORT_PURCHASE_ORDER_SUMMARY,
  REPORT_EXPORT_PURCHASE_ORDER_DETAIL,
} from '../../../common/constants/Reports.js'
import {reportSelector} from '../../../data/reports.js'
import {
  formSelector,
  getState,
  updateForm,
  useForm,
  useSelector,
} from '../../../store.js'
import {suppliersSortedByNameSelector} from '../../../data/suppliers.js'
import {warehousesSortedByNameSelector} from '../../../data/warehouses.js'
import Select from '../../../common/components/Select.js'
import {reportOptionsByValueSelector} from '../reportsPageSelectors.js'
import {PO_DATE_CREATED} from '../../../common/constants/PurchaseOrderListDateRangeFilters.js'
import {
  cleanPOQuery,
  getPODateRangeParams,
} from '../../PurchaseOrderListPage/purchaseOrderListSelectors.js'
import {ensureArray, ensureArrayOfNumbers} from '../../../common/ensure.js'
import {showPOQueryFilterModal} from '../../PurchaseOrderListPage/Modals/POListFilterModal/poListFilterModalFunctions.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import Radio from '../../../common/components/Radio.js'
import {Count, plural} from '../../../common/components/Plural.js'
import {poTagsSortedByNameSelector} from '../../../data/poTags.js'
import {poFilterList} from '../../PurchaseOrderListPage/POOmniBar.js'
import {tagColors} from '../../../common/colors.js'

/**
 * Take saved report params and transform them into a useable shape for querying.
 * It also translates old params from before we switched this report to use the
 * GET /order query param shape.
 *
 * @param {object} params object of params, either the old shape or one that
 *  mirrors GET /order query params
 * @returns {object} query object used by GET /order
 */
function translateParamsPOListQuery(params) {
  const poQuery = {
    ...params,
    ...getPODateRangeParams(
      params.start_date,
      params.end_date,
      params.date_type || PO_DATE_CREATED,
      params.interval_type,
      params.interval_amount,
      params.interval_trunc,
      params.timezone,
    ),
  }

  if (params.poStatus) {
    poQuery.status = ensureArray(params.poStatus)
  }

  if (params.supplierIDs) {
    poQuery.supplier_id = ensureArrayOfNumbers(params.supplierIDs)
  }

  if (params.warehouseIDs) {
    poQuery.warehouse_id = ensureArrayOfNumbers(params.warehouseIDs)
  }

  return cleanPOQuery(poQuery)
}

export function setupExportPOsForm(params = {}) {
  let {
    reportID,
    poQuery = {},
    queryTotalCount = null,
    selectedPOIDs = [],
  } = params

  const {params: reportParams} = reportID
    ? reportSelector(getState(), {reportID})
    : {params: poQuery}

  poQuery = translateParamsPOListQuery(reportParams)

  delete poQuery.sort
  delete poQuery.limit
  delete poQuery.offset

  const querySelection = selectedPOIDs.length ? 'selection' : 'query'

  return {
    querySelection,
    poQuery,
    queryTotalCount,
    selectedPOIDs,
  }
}

export function exportPOsErrorsSelector() {
  return {}
}

export function exportPOsPayloadSelector(state, {formName}) {
  const {reportType, poQuery, selectedPOIDs, querySelection} = formSelector(
    state,
    {formName},
  )

  const payload = {
    type: reportType,
    params: querySelection === 'selection' ? {po_id: selectedPOIDs} : poQuery,
  }

  return payload
}

async function editPOListQueryParams({formName}) {
  const form = formSelector(getState(), {formName})

  const poQuery = await showPOQueryFilterModal({
    query: form.poQuery,
    singleRun: form.singleRun,
  })

  if (!isEqual(poQuery, form.poQuery)) {
    updateForm(formName, {
      poQuery,
      queryTotalCount: null,
    })
  }
}

export function ExportPOParamsSummary({params}) {
  const suppliers = useSelector(suppliersSortedByNameSelector)
  const warehouses = useSelector(warehousesSortedByNameSelector)
  const poTags = useSelector(poTagsSortedByNameSelector)
  const filterListByType = useMemo(
    () => [
      ...poFilterList({
        poTags,
        suppliers,
        warehouses,
        intervalSummary: false,
        query: params,
      })
        .reduce((prev, filter) => {
          const list = prev.get(filter.type) || []

          list.push(filter)

          prev.set(filter.type, list)

          return prev
        }, new Map())
        .entries(),
    ],
    [suppliers, warehouses, poTags, params],
  )

  return (
    <>
      {filterListByType.length === 0 ? (
        <p className="fs-n0 lh-md margin-bottom-0">
          <em>No filters have been applied to the purchase orders.</em>
        </p>
      ) : (
        filterListByType.map(([type, list]) => (
          <div key={type} className="flex flex-wrap margin-bottom-3">
            {list.map(({type, label, color}, index) => (
              <div
                key={index}
                style={color ? tagColors(color) : {}}
                className={`omni-bar_value-type_${type} omni-bar__multi-value omni-bar__multi-value-modal`}
              >
                <div className="omni-bar__multi-value__label">{label}</div>
              </div>
            ))}
          </div>
        ))
      )}
    </>
  )
}

export default function ExportPOsForm({formName}) {
  const form = useForm(formName)
  const reportOptionsByValue = useSelector(reportOptionsByValueSelector)

  const filterLabel =
    form.queryTotalCount === null
      ? 'Export using custom filter set'
      : form.queryTotalCount === 0
        ? 'No POs will be exported'
        : plural(form.queryTotalCount)`Export${[' all', '']} ${Count} PO${[
            's',
          ]} in the current filter set`

  return (
    <ul className="list list--no-style">
      {form.reportID ? (
        <li className="list__item--form list__item--no-style">
          <p className="fs-00 margin-top-20 margin-bottom-10">
            <strong>
              Level of Detail:{' '}
              {form.reportType === REPORT_EXPORT_PURCHASE_ORDER_SUMMARY
                ? 'Order Summary'
                : 'Order Detail'}
            </strong>
          </p>
        </li>
      ) : (
        <li className="list__item--form list__item--no-style margin-bottom-15 padding-bottom-20">
          <Select
            label="Level of Detail"
            id="detail_level"
            value={form.reportType}
            onChange={(reportType) => updateForm(formName, {reportType})}
          >
            {[
              reportOptionsByValue[REPORT_EXPORT_PURCHASE_ORDER_SUMMARY],
              reportOptionsByValue[REPORT_EXPORT_PURCHASE_ORDER_DETAIL],
            ].map(({value, display}) => (
              <option key={value} value={value}>
                {display}
              </option>
            ))}
          </Select>
        </li>
      )}
      <li className="list__item--form list__item--wrap-order-report-filters divider--bottom margin-bottom-15 padding-bottom-20">
        <div className="flex margin-bottom-10">
          <strong className="fs-n0 lh-md margin-right-10 flex--justify-col">
            Current PO Filters
          </strong>
          <div>
            <ButtonPrimary
              isOutlined
              size="xx-sm"
              className="flex--justify-col"
              onClick={() => editPOListQueryParams({formName})}
            >
              Edit
            </ButtonPrimary>
          </div>
        </div>
        <ExportPOParamsSummary type={form.reportType} params={form.poQuery} />
      </li>
      {form.selectedPOIDs.length > 0 && (
        <li className="list__item--form margin-bottom-15">
          <Radio
            mode="fancy"
            label={plural(form.selectedPOIDs)`Export ${Count} ${[
              'records that are',
              'record that is',
            ]} currently selected`}
            id="query_selection__selection"
            checked={form.querySelection === 'selection'}
            onChange={() => updateForm(formName, {querySelection: 'selection'})}
          />
        </li>
      )}
      <li className="list__item--form margin-bottom-15">
        {form.selectedPOIDs.length > 0 ? (
          <Radio
            mode="fancy"
            label={filterLabel}
            id="query_selection__query"
            checked={form.querySelection === 'query'}
            onChange={() => updateForm(formName, {querySelection: 'query'})}
          />
        ) : (
          <strong className="fs-n0 lh-md">{filterLabel}.</strong>
        )}
      </li>
    </ul>
  )
}
